import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxFormComponent } from "devextreme-angular";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import DevExpress from "devextreme";
import { compact, forOwn, trim } from "lodash-es";
import { last } from "lodash";
import { lastValueFrom } from "rxjs";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { HealthRecordBackend } from "src/app/backend/api/health-record.backend";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  HealthRecord,
  HealthRecordWithAttachmentsAndTicketExtended
} from "src/app/backend/models/health-record";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";
import { Customer } from "src/app/backend/models/customer/customer";
import { Physician } from "src/app/backend/models/physician";
import { Pet } from "src/app/backend/models/pet/pet";
import { sntConfirm } from "src/app/common/dialog";
import { DocumentService } from "src/app/services/document.service";
import { PopupService } from "src/app/services/popup/popup.service";
import { PrintService } from "src/app/services/print/print.service";
import { ReportService } from "src/app/services/report.service";
import { TemplateService } from "src/app/services/template.service";
import { getMimeType } from "src/app/support/file";
import { soapTemplateWithHealthRecordoToTemplateItems } from "src/app/support/health-record";
import { Nullable } from "src/app/support/nullable";
import { pdfMergeDocuments } from "src/app/support/pdf";
import { sntTranslate } from "src/app/support/translate";
import { uuid } from "src/app/support/uuid";

@Injectable()
export class HealthRecordService {
  private readonly soapTemplateBackend = inject(SoapTemplateBackend);
  private readonly memberBackend = inject(MemberBackend);
  private readonly templateService = inject(TemplateService);
  private readonly patientBackend = inject(PetBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly reportService = inject(ReportService);
  private readonly documentService = inject(DocumentService);
  private readonly printService = inject(PrintService);
  private readonly healthRecordBackend = inject(HealthRecordBackend);
  private readonly popupService = inject(PopupService);
  private readonly physicianBackend = inject(PhysicianBackend);
  private readonly storageBackend = inject(StorageBackend);

  async generatePdfBlob({
    patientId,
    healthRecord,
    includeFollowUp
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
    includeFollowUp: boolean;
  }): Promise<Blob> {
    const healthRecordsToPrint = [healthRecord];

    if (includeFollowUp) {
      const children = await lastValueFrom(
        this.healthRecordBackend.listAllChildrenDeepExtendedWithAttachmentsByPatient(
          patientId,
          healthRecord.id
        )
      );

      children.forEach((x) => healthRecordsToPrint.push(x));
    }

    const blobs = [];

    for (const healthRecordToPrint of healthRecordsToPrint) {
      blobs.push(
        await this.healthRecordToLettterHeadPdfBlob(healthRecordToPrint)
      );
    }

    return await pdfMergeDocuments(blobs);
  }

  async toPdfHealthRecord({
    patientId,
    healthRecord,
    ignoreFollowUp
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
    ignoreFollowUp?: boolean;
  }): Promise<Blob | null> {
    return new Promise<Blob | null>((res) => {
      if (ignoreFollowUp) {
        const mergedPdfDocumentBlob = this.generatePdfBlob({
          patientId: patientId,
          healthRecord: healthRecord,
          includeFollowUp: false
        });

        res(mergedPdfDocumentBlob);
        return;
      }

      this.popupService.createEntity<{ includeFollowUp: boolean }>({
        title: sntTranslate(marker("Stampa visita")),
        initialFormData: {
          includeFollowUp: false
        },
        modalConfig: { dialog: true, height: 180 },
        formItems: [
          {
            dataField: "includeFollowUp",
            editorType: "dxCheckBox",
            label: {
              text: sntTranslate(marker("Includi i follow up")),
              showColon: false
            }
          },
          {
            template: () =>
              `<span class="text-slate-400 italic text-xs">${sntTranslate(
                marker(
                  "La stampa potrebbe impiegare fino ad un minuto se presenti più visite"
                )
              )}</span>`
          }
        ],
        create: async (data) => {
          const mergedPdfDocumentBlob = this.generatePdfBlob({
            patientId: patientId,
            healthRecord: healthRecord,
            includeFollowUp: data.includeFollowUp
          });

          res(mergedPdfDocumentBlob);
        },
        onCancel: async () => {
          res(null);
        }
      });
    });
  }

  async printHealthRecord({
    patientId,
    healthRecord
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
  }): Promise<void> {
    const blob = await this.toPdfHealthRecord({ patientId, healthRecord });

    if (blob) {
      await this.printService.printPdf(Promise.resolve(blob));
    }
  }

  /**
   * SOAP send dialog (email, PDF, attachments) shared by clinical diary and filter detail.
   * Uses the loaded patient for all fields (single source of truth).
   */
  async sendSoapHealthRecordWithDialog(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended
  ): Promise<void> {
    const patient: Pet = await lastValueFrom(
      this.patientBackend.load({ id: healthRecord.patient.id })
    );

    if (!healthRecord.completedAt && healthRecord.type === "soap") {
      const confirm = await sntConfirm(
        sntTranslate(marker("Diario clinico")),
        sntTranslate(marker("Sicuro di inviare un scheda ancora non conclusa?"))
      );

      if (!confirm) {
        return;
      }
    }

    const parents = await lastValueFrom(
      this.healthRecordBackend.listAllChildrenByPatient(
        patient.id,
        healthRecord.id
      )
    );

    const customUsersEmails: Record<string, string> = {};

    let referringPhysician: Physician | undefined;
    let externalPhysician: Physician | undefined;

    if (patient.referringPhysician) {
      referringPhysician = await lastValueFrom(
        this.physicianBackend.load(patient.referringPhysician)
      );
    }

    if (patient.externalPhysician) {
      externalPhysician = await lastValueFrom(
        this.physicianBackend.load(patient.externalPhysician)
      );
    }

    const attachmentsList = healthRecord.attachments ?? [];

    const sendableAttachments = attachmentsList.filter(
      (x) => !(<AttachmentV2>x).signable && !(<AttachmentV2>x).signature
    );

    const signatureSignedAttachments = attachmentsList.filter(
      (x) => !!(<AttachmentV2>x).signature?.completedAt
    );

    const useTheSignedVersionOfTheDocument =
      signatureSignedAttachments.length > 0 && !!healthRecord.completedAt;

    await this.popupService.createEntity<{
      email: string;
      includeFollowUp: boolean;
      note?: string;
      physician?: string;
      referringPhysician?: string;
      externalPhysician?: string;
    }>({
      title: sntTranslate(marker("Invio scheda")),
      initialFormData: { email: patient.owner?.email || "" },
      modalConfig: {
        dialog: true,
        width: 384,
        height: 320
      },
      formItems: compact([
        {
          itemType: "simple",
          dataField: "email",
          editorType: "dxSelectBox",
          editorOptions: {
            dataSource: new DataSource({
              store: new CustomStore({
                key: "email",
                byKey: async (options) => {
                  return patient.relatives?.find(
                    (relative) => relative.email === options
                  );
                },
                load: async (options) => {
                  return {
                    data: patient.relatives,
                    totalCount: patient.relatives?.length
                  };
                }
              })
            }),
            searchEnabled: "true",
            valueExpr: "email",
            itemTemplate: (data: Customer & { email: string }) => {
              const firstLine = trim(
                `${data.lastName || ""} ${data.firstName || ""}`
              );
              const secondLine = trim(data.email);

              return `<div class="mr-1 text-xl">
                        <i class="fa-light fa-user-circle"></i>
                      </div>
                      <div class="flex flex-col items-start justify-center">
                        ${firstLine ? `<span>${firstLine}</span>` : ""}
                        <span>${secondLine}</span>
                      </div>`;
            },
            displayExpr: (data: Nullable<Customer & { email?: string }>) => {
              return data?.email || "";
            },
            acceptCustomValue: true,
            onCustomItemCreating: (dxEvent: {
              customItem: Partial<Customer> & { email?: string };
              text: string;
            }) => {
              const email = dxEvent.text;
              const id = uuid();

              dxEvent.customItem = {
                id,
                email: email,
                firstName: "",
                lastName: "",
                number: ""
              };
              customUsersEmails[id] = email;
            }
          },
          validationRules: [{ type: "required" }, { type: "email" }],
          label: {
            text: sntTranslate(marker("Email")),
            location: "top",
            showColon: false
          }
        },
        {
          itemType: "simple",
          dataField: "note",
          editorType: "dxTextArea",
          label: {
            text: sntTranslate(marker("Nota")),
            location: "top",
            showColon: false
          },
          editorOptions: { height: 80, maxLength: 500 },
          validationRules: []
        },
        {
          visible: parents.length > 0,
          dataField: "includeFollowUp",
          editorType: "dxCheckBox",
          label: {
            text: sntTranslate(marker("Includi i follow up")),
            showColon: false
          }
        },
        {
          visible: patient.physician !== undefined,
          dataField: "physician",
          editorType: "dxCheckBox",
          label: {
            text: `${sntTranslate(marker("Medico curante"))}: ${
              patient.physician?.firstName
            } ${patient.physician?.lastName}`,
            showColon: false
          }
        },
        {
          visible: referringPhysician !== undefined,
          dataField: "referringPhysician",
          editorType: "dxCheckBox",
          label: {
            text: `${sntTranslate(marker("Medico referente"))}:  ${
              referringPhysician?.firstName
            } ${referringPhysician?.lastName}`,
            showColon: false
          }
        },
        {
          visible: externalPhysician !== undefined,
          dataField: "externalPhysician",
          editorType: "dxCheckBox",
          label: {
            text: `${sntTranslate(marker("Medico curante (esterno)"))}:  ${
              externalPhysician?.firstName
            } ${externalPhysician?.lastName}`,
            showColon: false
          }
        },
        sendableAttachments.length > 0
          ? {
              template: (dxEvent: any, element: HTMLElement) => {
                element.innerHTML = `<div class="flex flex-row items-center mt-4">
                <span class="text-sm text-slate-400">${sntTranslate(
                  marker("Seleziona gli allegati da includere nella mail:")
                )}</span></div>`;
              }
            }
          : null,
        sendableAttachments.length > 1
          ? <DevExpress.ui.dxForm.Item>{
              dataField: "selectAllAttachments",
              editorType: "dxCheckBox",
              editorOptions: {
                text: sntTranslate(marker("Seleziona tutti")),
                onValueChanged: ({ instance, formData }: DxFormComponent) => {
                  let selectAllAttachments: boolean =
                    formData["selectAllAttachments"];
                  sendableAttachments.forEach(({ id }) => {
                    instance.updateData(
                      `attachment-${id}`,
                      selectAllAttachments
                    );
                  });
                }
              },
              label: {
                text: sntTranslate(marker("Seleziona tutti")),
                location: "left",
                showColon: false,
                visible: false
              }
            }
          : null,
        ...(sendableAttachments.map(
          (x) =>
            <DevExpress.ui.dxForm.Item>{
              dataField: `attachment-${x.id}`,
              editorType: "dxCheckBox",
              editorOptions: {
                text: x.name,
                onValueChanged: ({ instance, formData }: DxFormComponent) => {
                  if (sendableAttachments.length > 1) {
                    instance.updateData(
                      "selectAllAttachments",
                      sendableAttachments.every(
                        ({ id }) => formData[`attachment-${id}`]
                      )
                    );
                  }
                }
              },
              label: {
                text: x.name,
                location: "left",
                showColon: false,
                visible: false
              }
            }
        ) || []),
        {
          template: () =>
            `<div class="mt-4">
                <span class="text-slate-400 italic text-xs">${sntTranslate(
                  marker(
                    "La stampa potrebbe impiegare fino ad un minuto se presenti più visite"
                  )
                )}</span>
             </div>`
        },
        useTheSignedVersionOfTheDocument
          ? {
              template: () =>
                `
                <div class="flex flex-row items-center gap-2">
                  <i class="fa-duotone fa-solid fa-circle-info"></i>

                  <span class="text-slate-400 italic text-xs">
                    ${sntTranslate(
                      marker(
                        "Al cliente verrà inviata la versione firmata della scheda"
                      )
                    )}
                  </span>
                </div>`
            }
          : null
      ]),
      create: async (data) => {
        let documentStorageId: string | undefined;

        switch (true) {
          case !useTheSignedVersionOfTheDocument: {
            const pdfBlob = await this.generatePdfBlob({
              patientId: patient.id,
              healthRecord: healthRecord,
              includeFollowUp: !!data.includeFollowUp
            });

            if (!healthRecord.storageId) {
              const uploadResult = await lastValueFrom(
                this.storageBackend.upload(
                  new File([pdfBlob], `${healthRecord.name}.pdf`),
                  null
                )
              );

              healthRecord.storageId = uploadResult.id;

              await lastValueFrom(
                this.healthRecordBackend.update(healthRecord.id, {
                  type: healthRecord.type,
                  storageId: uploadResult.id,
                  version: healthRecord.version
                })
              );
            } else {
              await lastValueFrom(
                this.storageBackend.update(
                  healthRecord.storageId,
                  `${healthRecord.name}.pdf`,
                  pdfBlob
                )
              );
            }

            documentStorageId = healthRecord.storageId;

            break;
          }
          case useTheSignedVersionOfTheDocument: {
            const lastSignedAttachment = last(signatureSignedAttachments)!;

            documentStorageId = lastSignedAttachment?.storageId;
            break;
          }
        }

        const attachments: Array<{
          contentType: string;
          fileName: string;
          storageId: string;
        }> = [];

        forOwn(data, (value, key) => {
          if (key && key.startsWith("attachment-") && value) {
            const attachmentId = last(key.split("-"));
            const attachment = attachmentsList.find(
              (x) => x.id === attachmentId
            )!;
            attachments.push({
              storageId: attachment.storageId,
              fileName: attachment.name,
              contentType:
                getMimeType(attachment.name) || "application/octet-stream"
            });
          }
        });

        if (data.referringPhysician) {
          if (!referringPhysician?.email) {
            throw new Error("Il medico referente non ha impostata la mail.");
          }

          await this.healthRecordBackend.send(
            <HealthRecord>(<unknown>healthRecord),
            {
              email: <string>referringPhysician.email,
              storageId: healthRecord.storageId,
              customerId: patient.owner?.id,
              attachments,
              note: <string | undefined>data.note
            }
          );
        }

        if (data.externalPhysician) {
          if (!externalPhysician?.email) {
            throw new Error(
              "Il medico curante esterno non ha impostata la mail."
            );
          }

          await this.healthRecordBackend.send(
            <HealthRecord>(<unknown>healthRecord),
            {
              email: <string>externalPhysician.email,
              storageId: healthRecord.storageId,
              customerId: patient.owner?.id,
              attachments,
              note: <string | undefined>data.note
            }
          );
        }

        if (data.physician) {
          if (!patient.physician?.id) {
            throw new Error("Medico curante non disponibile.");
          }

          const physicianMember = await lastValueFrom(
            this.memberBackend.load(patient.physician.id)
          );

          if (!physicianMember.email) {
            throw new Error("Il medico curante non ha impostata la mail.");
          }

          await this.healthRecordBackend.send(
            <HealthRecord>(<unknown>healthRecord),
            {
              email: <string>physicianMember.email,
              storageId: healthRecord.storageId,
              customerId: patient.owner?.id,
              attachments,
              note: <string | undefined>data.note
            }
          );
        }

        await this.healthRecordBackend.send(
          <HealthRecord>(<unknown>healthRecord),
          {
            email: <string>data.email,
            storageId: documentStorageId,
            customerId: patient.owner?.id,
            attachments,
            note: <string | undefined>data.note
          }
        );
      }
    });
  }

  async healthRecordToHTML(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended
  ): Promise<string> {
    const soapTemplate = await lastValueFrom(
      this.soapTemplateBackend.load(
        (<HealthRecord>(<unknown>healthRecord)).detail!.templateId!
      )
    );

    const pet = healthRecord.patient
      ? await lastValueFrom(
          this.patientBackend.load({ id: healthRecord.patient.id })
        )
      : undefined;

    const template = soapTemplateWithHealthRecordoToTemplateItems(
      soapTemplate,
      <HealthRecord>(<unknown>healthRecord),
      healthRecord.attachments,
      this.memberBackend,
      this.physicianBackend,
      this.templateService,
      this.patientBackend,
      this.customerBackend,
      undefined,
      pet
    );

    const { content } = await this.reportService.templateToHTML(
      template,
      {
        authors: healthRecord.staff?.map((x) => x.id) || []
      },
      {
        renderer: "data",
        componentsRenderers: [],
        headerTitle: healthRecord.name || "",
        useFakeTextData: false,
        onlyTextWithValue: true
      }
    );

    return content;
  }

  private async healthRecordToLettterHeadPdfBlob(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended
  ): Promise<Blob> {
    const content = await this.healthRecordToHTML(healthRecord);
    return await this.documentService.letterHeadDocunentToPdf(content);
  }
}
